import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Order } from '@models/order';
import { formatNumber } from '@angular/common';
import { SettingsService } from '@services/settings.service';
import { ReturnJob } from '@classes/return-job';

export interface ReturnOrderDialogData {
  message: string;
  order: Order;
  /** When true, only show credit (e.g. void). Hides fee / inventory options. */
  creditOnly?: boolean;
  /** Exchange keeps the original invoice and creates a linked job. */
  mode?: 'return' | 'exchange';
}

export interface ReturnOrderDialogResult {
  credit: number;
  restockingFee: number;
  netCredit: number;
  returnToInventory: boolean;
  applyRestockingFee?: boolean;
  restockingFeePercent?: number;
}

@Component({
  selector: 'app-return-order-dialog',
  templateUrl: './return-order-dialog.component.html',
  styleUrls: ['./return-order-dialog.component.css'],
})
export class ReturnOrderDialogComponent {
  credit: any = 0;
  restockingFee: any = '0.00';
  returnToInventory = true;
  creditOnly = false;
  isExchange = false;
  isReturn = false;
  applyRestockingFee = true;
  restockingFeePercent = 15;
  eligibleAmount = 0;

  constructor(
    public dialogRef: MatDialogRef<ReturnOrderDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ReturnOrderDialogData,
    private settingsService: SettingsService
  ) {
    this.creditOnly = !!data?.creditOnly;
    this.isExchange = data?.mode === 'exchange';
    this.isReturn = !this.creditOnly && !this.isExchange;
    this.returnToInventory = this.isReturn || this.isExchange;
    const order: Order = data.order;
    this.eligibleAmount = ReturnJob.eligibleAmount(order);
    const percent = Number(this.settingsService.restockingFeePercent);
    this.restockingFeePercent =
      isNaN(percent) || percent < 0 ? 15 : percent;
    this.credit =
      this.eligibleAmount === 0
        ? '0.00'
        : this.formatNumberValue(this.eligibleAmount);
  }

  get restockingFeeAmount(): number {
    if (!this.isReturn || !this.applyRestockingFee) {
      return 0;
    }
    return ReturnJob.restockingFeeAmount(
      this.eligibleAmount,
      this.restockingFeePercent
    );
  }

  get netCredit(): number {
    if (this.isReturn) {
      return Math.max(0, this.eligibleAmount - this.restockingFeeAmount);
    }
    return Math.max(
      0,
      this.parseCurrency(this.credit) - this.parseCurrency(this.restockingFee)
    );
  }

  get netCreditDisplay(): string {
    return formatNumber(this.netCredit, 'en-US', '1.2-2');
  }

  get eligibleDisplay(): string {
    return formatNumber(this.eligibleAmount, 'en-US', '1.2-2');
  }

  get restockingFeeDisplay(): string {
    return formatNumber(this.restockingFeeAmount, 'en-US', '1.2-2');
  }

  get confirmLabel(): string {
    return this.isExchange ? 'EXCHANGE' : 'RETURN';
  }

  get canConfirm(): boolean {
    if (this.creditOnly) {
      return true;
    }
    if (this.isExchange || this.isReturn) {
      return this.returnToInventory;
    }
    return true;
  }

  onNoClick(): void {
    this.dialogRef.close(false);
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  return(): void {
    if (!this.canConfirm) {
      return;
    }
    this.dialogRef.close(this.buildResult());
  }

  /** Kept for callers that still read componentInstance after close. */
  buildResult(): ReturnOrderDialogResult {
    if (this.isReturn) {
      return {
        credit: this.eligibleAmount,
        restockingFee: this.restockingFeeAmount,
        netCredit: this.netCredit,
        returnToInventory: true,
        applyRestockingFee: this.applyRestockingFee,
        restockingFeePercent: this.restockingFeePercent,
      };
    }
    const credit = this.parseCurrency(this.credit);
    const restockingFee = this.creditOnly
      ? 0
      : this.parseCurrency(this.restockingFee);
    const netCredit = Math.max(0, credit - restockingFee);
    return {
      credit,
      restockingFee,
      netCredit,
      returnToInventory: this.creditOnly
        ? false
        : this.isExchange
        ? true
        : this.returnToInventory,
    };
  }

  formatCurrency(field: 'credit' | 'restockingFee' = 'credit'): void {
    if (field === 'restockingFee') {
      this.restockingFee = this.formatNumberValue(this.restockingFee);
      return;
    }
    this.credit = this.formatNumberValue(this.credit);
  }

  private formatNumberValue(value: any): string {
    return formatNumber(this.parseCurrency(value), 'en-US', '1.2-2');
  }

  private parseCurrency(value: any): number {
    if (typeof value === 'number') {
      return isNaN(value) ? 0 : value;
    }
    const parsed = parseFloat(String(value ?? '0').replace(/,/g, ''));
    return isNaN(parsed) ? 0 : parsed;
  }
}
